import React, { useCallback, useMemo, useState } from 'react'
import { useSharedContext } from '@leverege/ui-plugin'
import { DateTime } from 'luxon'
import { Flex, Text } from '@leverege/ui-elements'

import { OperatingHours } from '@leverege/operating-hours'
import { Constants, getOpenHoursFromOperatingHours } from '../../../../util'

import S from './useTrafficTable.css'

export default function useTrafficTable( props ) {
  const { events } = props

  const { operatingHours } = useSharedContext()
  const [ type, setType ] = useState( 'count' )
  const onChangeType = useCallback( ( { data } ) => {
    setType( data )
  }, [] )

  const { data } = useMemo( () => {
    const openHours = getOpenHoursFromOperatingHours( operatingHours, false )
    const timezone = OperatingHours.getTimezone( operatingHours )
    openHours.push( 'total' )

    const ret = openHours.reduce( ( prev, hour ) => {
      const dayData = Constants.DAYS_OF_WEEK.reduce( ( prev, day ) => {
        prev[day] = {
          dog : 0,
          person : 0,
          dates : new Set()
        }

        return prev
      }, {} )

      if ( hour === 'total' ) {
        prev[hour] = {
          label : 'Total',
          events : [],
          total : {
            person : 0,
            dog : 0,
            dates : new Set()
          },
          days : {
            ...dayData
          }
        }
      } else {
        const start = parseInt( hour )
        const end = start + 1

        prev[hour] = {
          start,
          end,
          label : formatStartEnd( { start, end } ),
          events : [],
          total : {
            person : 0,
            dog : 0,
            dates : new Set()
          },
          days : {
            ...dayData
          }
        }
      }

      return prev
    }, {} )

    const start = DateTime.fromISO( events.start )
    const end = DateTime.fromMillis(
      Math.min(
        new Date( events.end ),
        DateTime.local().setZone( operatingHours.timezone, { keepLocalTime : true } )
      )
    )

    for ( let i = start; i < end; i = i.plus( { hour : 1 } ) ) {
      const hour = i.get( 'hour' )
      const weekDay = i.get( 'weekdayLong' )
      const date = i.toISODate()

      if ( ret[`${hour}`]?.days?.[weekDay]?.dates ) {
        ret[`${hour}`].days[weekDay].dates.add( date )
        ret.total.days[weekDay].dates.add( date )
        ret.total.total.dates.add( date )
        ret[`${hour}`].total.dates.add( date )
      }
    }

    events?.items?.forEach( ( item ) => {
      const { time, data } = item
      const dateTime = DateTime
        .fromISO( time )
        .setZone( timezone )
      const hour = dateTime.get( 'hour' )
      const weekDay = dateTime.get( 'weekdayLong' )

      if ( ret[`${hour}`] ) {
        ret[`${hour}`].events.push( item )
        ret.total.events.push( item )

        if ( data.data?.changes?.person != null ) {
          ret[`${hour}`].total.person += 1
          ret.total.total.person += 1
        }

        if ( data.data?.changes?.dog != null ) {
          ret[`${hour}`].total.dog += 1
          ret.total.total.dog += 1
        }

        if ( ret[`${hour}`].days[weekDay] ) {
          if ( data.data?.changes?.person != null ) {
            ret[`${hour}`].days[weekDay].person += 1
            ret.total.days[weekDay].person += 1
          }
  
          if ( data.data?.changes?.dog != null ) {
            ret[`${hour}`].days[weekDay].dog += 1
            ret.total.days[weekDay].dog += 1
          }
        }
      }
    } )

    const d = Object
      .entries( ret )
      .map( ( [ hour, data ] ) => {
        return {
          ...data,
          id : data.label
        }
      } )
    
    return {
      data : d
    }
  }, [ events, operatingHours ] )

  const columns = useMemo( () => {
    const dayColumns = Constants.DAYS_OF_WEEK.reduce( ( prev, day ) => {
      prev.push( {
        id : day,
        Header : day,
        accessor : `days.${day}`,
        Cell : props => <DayValueRenderer type={type} {...props} />
      } )

      return prev
    }, [] )
    
    return [ {
      id : 'time',
      Header : 'Time',
      sticky : 'left',
      accessor : 'label',
      Cell : ( { value } ) => <Text variant="columnLabel">{value}</Text>
    }, {
      id : 'totals',
      Header : 'Total',
      sticky : 'left',
      accessor : 'total',
      Cell : props => <DayValueRenderer type={type} {...props} />
    }, ...dayColumns ]
  }, [ type ] )

  return {
    data,
    columns,
    type,
    onChangeType
  }
}

function DayValueRenderer( props ) {
  const { value, column, row, rowsById, type = 'count' } = props
  const { Total : { original } } = rowsById
  const { id : columnId } = column
  const { id : rowId } = row

  let rowPersonValue = value.person === 0 ?
    0 :
    type === 'count' ?
      value.person :
      Math.round( value.person / value.dates.size )
  let rowDogValue = value.dog === 0 ?
    0 :
    type === 'count' ?
      value.dog :
      Math.round( value.dog / value.dates.size )

  rowPersonValue = Math.round( rowPersonValue )
  rowDogValue = Math.round( rowDogValue )

  const o = original.total

  if ( !value ) {
    return (
      <Flex variant="rowM" className={S.dayValue}>
        <Text variant="pillPersonEmpty">-</Text>
        <Text variant="pillDogEmpty">-</Text>
      </Flex>
    )
  }

  if ( columnId === 'totals' || rowId === 'Total' ) {
    return (
      <Flex variant="rowM" className={S.dayValue}>
        <Text variant="pillPersonEmpty">{rowPersonValue || '-'}</Text>
        <Text variant="pillDogEmpty">{rowDogValue || '-'}</Text>
      </Flex>
    )
  }

  return (
    <Flex variant="rowM" className={S.dayValue}>
      <Text variant={calcTrafficVariant( value.person, o.person, 'person' )}>{rowPersonValue || '-'}</Text>
      <Text variant={calcTrafficVariant( value.dog, o.dog, 'dog' )}>{rowDogValue || '-'}</Text>
    </Flex>
  )
}

function formatStartEnd( item ) {
  const { start, end } = item

  let s, e

  if ( start < 12 ) {
    s = `${start}AM`
  } else if ( start === 24 ) {
    s = '12AM'
  } else if ( start === 12 ) {
    s = '12PM'
  } else {
    s = `${start - 12}PM`
  }

  if ( end < 12 ) {
    e = `${end}AM`
  } else if ( end === 24 ) {
    e = '12AM'
  } else if ( end === 12 ) {
    e = '12PM'
  } else {
    e = `${end - 12}PM`
  }

  return `${s} - ${e}`
}

const TRAFFIC_LOWER_BOUND = 0.012
const TRAFFIC_UPPER_BOUND = 0.015

function calcTrafficVariant( value, total, type ) {
  if ( total === 0 ) {
    return `pill${type[0].toUpperCase()}${type.slice( 1 )}Empty`
  }

  const lower = total * TRAFFIC_LOWER_BOUND
  const upper = total * TRAFFIC_UPPER_BOUND

  const modifier = value < lower ? 'Empty' :
    value >= lower && value <= upper ? '' :
      'Dark'

  return `pill${type[0].toUpperCase()}${type.slice( 1 )}${modifier}`
}
